
import { SubscriptionBillingType } from "@/models/subscription/SubscriptionPlan";
import { encodeExchangeName } from "@/utils/exchangeUtils";
import { showInformationSnackbar } from "@/utils/snackbarUtils";
import BaseAuthenticatedView from "@/views/BaseAuthenticatedView.vue";
import { Component, Watch } from "vue-property-decorator";
import Vue from "vue";
import { Emit } from "@/api/apiHelper";
import { Exchange } from "@/models/Exchange";

@Component({
  components: {
    UpgradeSubscriptionDialog: () =>
      import(
        "@/components/subscription/upgrade-subscription-plan/UpgradeSubscriptionDialog.vue"
      ),
    SelectExchangeStep: () => import("./steps/SelectExchange.vue"),
    MockPortfolioOverviewStep: () =>
      import("./steps/MockPortfolioOverview.vue"),
    ConnectExchangeStep: () => import("./steps/ConnectExchange.vue"),
    PortfolioAllocationStep: () => import("./steps/PortfolioAllocation.vue"),
    PortfolioOverviewStep: () => import("./steps/PortfolioOverview.vue"),
  },
})
export default class CreateTradingBot extends BaseAuthenticatedView {
  // 📦 Currently displayed onboarding step
  private currentStep: number = 1;

  // 🚩 Flag for whether the page is loading
  private isLoading: boolean = false;

  // 🚩 Flag for whether the create subscription dialog should be shown
  private showCreateSubscriptionDialog: boolean = false;

  // 📦 The create subscription upgrade type
  private createSubscriptionUpgradeType: string = "normal";

  // 🚩 Flag for whether a portfolio is being created
  private isCreatingPortfolio: boolean = false;

  // 📦 The exchange on which the user want to base their portfolio
  private selectedExchange: Exchange | null = null;

  // 📦 The selected investment amount
  private selectedInvestmentAmount: number | null = null;

  // 📦 The selected base currency (USD, USDT, etc.) for investment portfolio
  private selectedBaseCurrency: string | null = null;

  // 📦 Flow for create investment portfolio
  private get CREATE_PORTFOLIO_STEPS(): any {
    const connectedExchangeNames = this.$store.state.exchanges.connectedExchanges.map(
      (exchange: Exchange) => exchange.name
    );
    let steps = { SELECT_EXCHANGE: 1 };

    if (this.$store.state.bots.allBots.length == 0) {
      steps["MOCK_PORTFOLIO_OVERVIEW"] = Object.entries(steps).length + 1;
    }

    if (
      this.selectedExchange != null &&
      !connectedExchangeNames.includes(this.selectedExchange?.name)
    ) {
      steps["CONNECT_EXCHANGE"] = Object.entries(steps).length + 1;
    }

    steps["ALLOCATION"] = Object.entries(steps).length + 1;
    steps["OVERVIEW"] = Object.entries(steps).length + 1;

    return steps;
  }

  protected async mounted() {
    super.mounted();
    this.isLoading = true;
    await this.$store.dispatch("fetchAllExchanges");
    this.isLoading = false;

    // Skip step if the user has connected just 1 exchange so far (simplifies the flow)
    let connectedExchanges = this.$store.state.exchanges.connectedExchanges;
    if (connectedExchanges.length == 1) {
      this.selectedExchange = connectedExchanges[0];
      this.currentStep = 2;
    }
  }

  /**
   * 🚩Flag for whether the selected investment amount is valid
   */
  private isInvestmentAmountValid() {
    let subscriptionPlan = this.$store.state.subscription
      .currentSubscriptionPlan;

    // Check if the balance in use exceeds the tier limits
    if (
      Number(this.selectedInvestmentAmount) +
        Number(this.$store.getters.totalStartingBalance) >
      Number(subscriptionPlan.maxTotalInUse)
    ) {
      if (
        subscriptionPlan.billingType === SubscriptionBillingType.Free &&
        !subscriptionPlan.freePro
      ) {
        this.createSubscriptionUpgradeType = "upgrade-balance";
        this.showCreateSubscriptionDialog = true;
        return false;
      } else {
        // Paid user that exceeds the balance in use limits
        showInformationSnackbar(
          "Your current plan supports up to $" +
            subscriptionPlan.maxTotalInUse.toFixed(2) +
            " in the total allocation. Contact us for an upgrade!"
        );
        return false;
      }
    }
    return true;
  }

  /**
   * ➕ Step increaser with a small delay
   */
  private increaseStepDelayed() {
    setTimeout(() => this.currentStep++, 50);
  }

  private delayed(func: () => void, delay = 50) {
    setTimeout(func, delay);
  }

  /**
   * 🚀 Creates an investment portfolio
   */
  private createBotPortfolio() {
    if (!this.selectedExchange || !this.isInvestmentAmountValid()) {
      return;
    }

    this.isCreatingPortfolio = true;
    Vue.prototype.$api
      .post(
        "api/v1/createmultibots",
        {
          exchange: encodeExchangeName(this.selectedExchange.name),
          balance: Number(this.selectedInvestmentAmount),
          quote_currency: this.selectedBaseCurrency,
        },
        Emit.All,
        true
      )
      .then((response: any) => this.$router.push("/my-portfolio"))
      .catch((error: any) => {
        this.showSuccessSnackbar(
          "Your portfolio creation has been successful! Let us finish setting up everything in the next minute. After that, you will be able to see all of your bots on your portfolio overview page after refreshing"
        );
        this.$router.push("/my-portfolio");
      })
      .finally(() => (this.isCreatingPortfolio = false));
  }
}
